'use client';

import { CheckIcon, HighlighterIcon, LightbulbIcon, LockBadgeIcon, UndoIcon } from './icons';
import React, { FC, useEffect, useRef } from 'react';
import { cn } from './cn';
import { Button } from './Button';
import { UNDO_HOLD_MS, type HighlightConfig, type HintBoxState } from '@puzzlio/core';

/** Undoes a move when clicked and, given `onUndoLongPressed`, does that instead when held down. */
export const UndoButton: FC<{
  onUndoClicked: () => void;
  onUndoLongPressed?: () => void;
  className?: string;
}> = ({ onUndoClicked, onUndoLongPressed, className }) => {
  const holdTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const longPressed = useRef(false);
  const cancelHold = () => clearTimeout(holdTimer.current);
  useEffect(() => cancelHold, []);

  return (
    <Button
      variant="gameplay"
      size="gameplay-sm"
      onPointerDown={(e) => {
        // A second finger landing mid-press is no hold, and mustn't leave the first one's timer running.
        cancelHold();
        longPressed.current = false;
        if (!onUndoLongPressed || e.button !== 0 || !e.isPrimary) return;
        holdTimer.current = setTimeout(() => {
          longPressed.current = true;
          onUndoLongPressed();
        }, UNDO_HOLD_MS);
      }}
      onPointerUp={cancelHold}
      onPointerLeave={cancelHold}
      onPointerCancel={cancelHold}
      // Releasing a long press still clicks, which mustn't also undo.
      onClick={() => !longPressed.current && onUndoClicked()}
      // A held touch otherwise selects the label or opens the browser's menu.
      onContextMenu={(e) => onUndoLongPressed && e.preventDefault()}
      className={cn('ph-no-autocapture group gap-1 select-none [-webkit-touch-callout:none]', className)}
    >
      <UndoIcon className="group-active:fill-yellow-s2 with-hover:group-hover:fill-orange size-[18px] md:size-5" />
      Undo
    </Button>
  );
};

export const ResetButton: FC<{
  onResetClicked: () => void;
  className?: string;
}> = ({ onResetClicked, className }) => {
  return (
    <Button
      variant="gameplay"
      size="gameplay-sm"
      onClick={onResetClicked}
      className={cn('ph-no-autocapture', className)}
    >
      Reset
    </Button>
  );
};

export const HintButton: FC<{
  onHintClicked: (e: React.MouseEvent<HTMLButtonElement>) => void;
  pressed?: boolean;
  // Flashes the bulb a few times, to draw the player's eye to the button.
  glow?: boolean;
  className?: string;
}> = ({ onHintClicked, pressed = false, glow = false, className }) => {
  return (
    <Button
      variant="gameplay"
      size="gameplay-sm"
      pressed={pressed}
      onClick={onHintClicked}
      className={cn('ph-no-autocapture group gap-1', className)}
    >
      <LightbulbIcon
        className={cn(
          'group-active:fill-yellow-s2 with-hover:group-hover:fill-orange size-[18px] md:size-5',
          pressed && 'fill-yellow-s2 with-hover:group-hover:fill-yellow-s2',
          glow && 'animate-hint-glow'
        )}
      />
      Hint
    </Button>
  );
};

export const CheckButton: FC<{
  onCheckClicked: (e: React.MouseEvent<HTMLButtonElement>) => void;
  pressed?: boolean;
  className?: string;
}> = ({ onCheckClicked, pressed = false, className }) => {
  return (
    <Button
      variant="gameplay"
      size="gameplay-sm"
      pressed={pressed}
      onClick={onCheckClicked}
      className={cn('ph-no-autocapture group gap-1', className)}
    >
      <CheckIcon
        className={cn(
          'group-active:fill-yellow-s2 with-hover:group-hover:fill-orange size-[18px] md:size-5',
          pressed && 'fill-yellow-s2 with-hover:group-hover:fill-yellow-s2'
        )}
      />
      Check
    </Button>
  );
};

/** A single circle button representing one highlight mode. The erase mode shows a red slash. */
export const HighlightCircleButton: FC<{
  color: string; // CSS color for the inner filled circle
  label: string; // accessible label
  active: boolean;
  erase?: boolean; // when true, render a red slash instead of a filled color circle
  onClick: () => void;
  className?: string;
}> = ({ color, label, active, erase = false, onClick, className }) => {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'ph-no-autocapture flex size-11 touch-none items-center justify-center rounded-full bg-white transition-[border-width,border-color] select-none',
        active ? 'border-[4px] border-black' : 'border-gray-4 border-3',
        className
      )}
    >
      {erase ? (
        <svg viewBox="0 0 28 28" fill="none" className="text-coral size-7">
          <line x1="6" y1="22" x2="22" y2="6" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        </svg>
      ) : (
        <div className="size-7 rounded-full" style={{ backgroundColor: color }} />
      )}
    </button>
  );
};

/** Row of highlight/erase circle buttons. Renders nothing when `highlightConfigs` is empty. */
export const HighlightButtons: FC<{
  highlightMode: string;
  highlightConfigs: Record<string, HighlightConfig>;
  setHighlightMode: (mode: string) => void;
  className?: string;
}> = ({ highlightMode, highlightConfigs, setHighlightMode, className }) => {
  const entries = Object.entries(highlightConfigs);
  if (entries.length === 0) return null;
  return (
    <div className={cn('flex justify-center', className)}>
      <div className="relative flex items-center gap-2 rounded-[22px] border-[3px] border-black bg-white px-2.5 py-2 shadow-[2px_3px_0px_0px_var(--yellow-600)]">
        {entries.map(([key, config]) => (
          <HighlightCircleButton
            key={key}
            color={config.color}
            label={config.label}
            active={highlightMode === key}
            erase={key === 'erase'}
            onClick={() => setHighlightMode(highlightMode === key ? '' : key)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Pencil toggle button that shows/hides the highlight buttons. When `locked` (the player
 * isn't a subscriber) a small padlock badge is shown and tapping instead reveals the
 * subscription promo, but the button otherwise looks and toggles the same.
 */
export const HighlightToggleButton: FC<{
  active: boolean;
  onClick: () => void;
  locked?: boolean;
  className?: string;
}> = ({ active, onClick, locked = false, className }) => {
  return (
    <div className={cn('relative', className)}>
      <Button
        variant="gameplay"
        size="gameplay-sm"
        pressed={active}
        onClick={onClick}
        aria-label="Toggle highlighter"
        aria-pressed={active}
        // Fixed square sized to match the sibling buttons' height so the icon-only button stays
        // square; aspect-square doesn't work here since flexbox sizes the width from its content.
        className={'ph-no-autocapture group size-[48px] md:size-[58px]'}
      >
        <HighlighterIcon
          className={cn(
            'with-hover:group-hover:[&_path]:fill-orange size-[18px] md:size-5',
            active && '[&_path]:fill-yellow-s2'
          )}
        />
      </Button>
      {locked && (
        <LockBadgeIcon className="pointer-events-none absolute -top-2 -right-2 z-10 size-6 md:-top-3 md:-right-3 md:size-8" />
      )}
    </div>
  );
};

export const GameButtons: FC<{
  onUndoClicked?: () => void;
  onUndoLongPressed?: () => void;
  onHintClicked?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onCheckClicked?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  // When supplied, a pencil toggle button is included in the row; `highlightActive` reflects its state.
  onHighlightToggled?: () => void;
  highlightActive?: boolean;
  // When true, the highlighter toggle shows a padlock badge (non-subscriber promo variant).
  highlightLocked?: boolean;
  pressedButton: HintBoxState;
  hintGlow?: boolean;
  className?: string;
}> = ({
  onUndoClicked,
  onUndoLongPressed,
  onHintClicked,
  hintGlow = false,
  onCheckClicked,
  onHighlightToggled,
  highlightActive = false,
  highlightLocked = false,
  pressedButton,
  className,
}) => {
  return (
    <div className={cn('flex w-full max-w-max justify-center gap-5', className)}>
      {onUndoClicked && <UndoButton onUndoClicked={onUndoClicked} onUndoLongPressed={onUndoLongPressed} />}
      {onHintClicked && <HintButton onHintClicked={onHintClicked} pressed={pressedButton === 'hint'} glow={hintGlow} />}
      {onCheckClicked && <CheckButton onCheckClicked={onCheckClicked} pressed={pressedButton === 'check'} />}
      {onHighlightToggled && (
        <HighlightToggleButton active={highlightActive} onClick={onHighlightToggled} locked={highlightLocked} />
      )}
    </div>
  );
};
